import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { ClockCounterClockwise, Download, Gear, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESOURCE_TYPE_LABELS = {
  auth: 'Authentication (Login/Logout)',
  user: 'User Management',
  branch: 'Branch Management',
  record: 'Records',
  document: 'Documents',
  data_structure: 'Data Structures',
  settings: 'Settings',
  temporary_link: 'Temporary Links',
};

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [resourceType, setResourceType] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [auditSettings, setAuditSettings] = useState(null);
  const [disabledTypes, setDisabledTypes] = useState([]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      if (resourceType) params.append('resource_type', resourceType);

      const { data } = await axios.get(`${API_URL}/api/audit-logs?${params}`);
      setLogs(data.logs);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (e) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, resourceType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const fetchAuditSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/audit-settings`);
      setAuditSettings(data);
      setDisabledTypes(data.disabled_resource_types || []);
    } catch (e) {
      toast.error('Failed to fetch audit settings');
    }
  };

  const handleOpenSettings = () => {
    fetchAuditSettings();
    setSettingsOpen(true);
  };

  const handleToggleResourceType = (type) => {
    setDisabledTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API_URL}/api/audit-settings`, disabledTypes, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success('Audit settings saved');
      setSettingsOpen(false);
    } catch (e) {
      toast.error('Failed to save audit settings');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/api/audit-logs/export?format=${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded');
    } catch (e) {
      toast.error('Failed to export logs');
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': case 'upload': case 'bulk_upload': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'update': case 'email_update': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'delete': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'login': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Audit Logs</h1>
          <p className="text-muted-foreground">
            System audit trail {total > 0 && <span className="text-xs">({total} entries)</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleOpenSettings} data-testid="audit-settings-btn">
            <Gear size={18} className="mr-2" />
            Settings
          </Button>
          <Button variant="outline" onClick={() => handleExport('csv')} data-testid="export-csv">
            <Download size={18} className="mr-2" />
            CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('json')} data-testid="export-json">
            <Download size={18} className="mr-2" />
            JSON
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="w-56">
              <Label className="text-xs text-muted-foreground mb-1 block">Resource Type</Label>
              <Select value={resourceType || "all"} onValueChange={(v) => { setResourceType(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="filter-resource-type">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {Object.entries(RESOURCE_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="spinner w-8 h-8 mx-auto"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length > 0 ? (
                  logs.map(log => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs">
                        {log.user_name || log.user_id?.slice(0, 8) + '...'}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs">
                          <Badge variant="outline" className="text-xs mr-1">{log.resource_type}</Badge>
                          <span className="font-mono text-muted-foreground">{log.resource_id?.slice(0, 8)}</span>
                        </span>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="text-xs text-muted-foreground truncate">
                          {log.old_value && (
                            <span className="text-destructive">-{JSON.stringify(log.old_value).slice(0, 40)}</span>
                          )}
                          {log.new_value && (
                            <span className="text-green-600 ml-1">+{JSON.stringify(log.new_value).slice(0, 40)}</span>
                          )}
                          {!log.old_value && !log.new_value && '-'}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {log.ip_address || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <ClockCounterClockwise size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-muted-foreground">No audit logs found</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ArrowLeft size={16} className="mr-1" /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Audit Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Audit Logging Settings</DialogTitle>
            <DialogDescription>Enable or disable audit logging for specific resource types</DialogDescription>
          </DialogHeader>
          {auditSettings ? (
            <div className="space-y-4">
              <div className="space-y-3">
                {Object.entries(RESOURCE_TYPE_LABELS).map(([key, label]) => {
                  const isEnabled = !disabledTypes.includes(key);
                  return (
                    <div
                      key={key}
                      className={`flex items-center justify-between p-3 rounded-md border transition-colors ${isEnabled ? 'bg-secondary/30 border-border' : 'bg-destructive/5 border-destructive/20'}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={isEnabled}
                          onCheckedChange={() => handleToggleResourceType(key)}
                          data-testid={`audit-toggle-${key}`}
                        />
                        <div>
                          <p className="text-sm font-medium">{label}</p>
                          <p className="text-xs text-muted-foreground">
                            {isEnabled ? 'Logging enabled' : 'Logging disabled'}
                          </p>
                        </div>
                      </div>
                      <Badge variant={isEnabled ? 'default' : 'destructive'} className="text-xs">
                        {isEnabled ? 'ON' : 'OFF'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveSettings} data-testid="save-audit-settings-btn">
                  Save Settings
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="spinner w-8 h-8 mx-auto"></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogsPage;
